import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, Edit3 } from 'lucide-react';

const BookieCard = ({ bookie, onEdit }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  if (!bookie) {
    return null;
  }

  const renderBooleanValue = (value) => (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
      value ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'
    }`}>
      {value ? 'Yes' : 'No'}
    </span>
  );

  const renderClones = (clones) => {
    if (!clones || clones.length === 0) {
      return <span className="text-gray-400 text-sm">None</span>;
    }

    return (
      <div className="space-y-1">
        {clones.map((clone, index) => (
          <div key={index} className="flex items-center space-x-2 text-sm">
            <span className="text-white font-medium">
              {typeof clone === 'string' ? clone : clone.bookie}
            </span>
            {typeof clone === 'object' && (
              <div className="flex space-x-1">
                {clone.runner !== undefined && (
                  <span className={`px-1 py-0.5 rounded-full text-xs ${
                    clone.runner ? 'bg-white text-[#1B2D1A]' : 'bg-gray-600 text-white'
                  }`}>
                    {clone.runner ? 'Runner' : 'No Runner'}
                  </span>
                )}
                {clone.restrictProxy && (
                  <span className="px-1 py-0.5 rounded-full text-xs bg-orange-500 text-white">
                    Restrict Proxy
                  </span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderMiddleConfiguration = (middle) => {
    if (!middle) return null;

    return (
      <div className="mt-4 p-4 bg-[#0F1A0E] rounded-lg">
        <h5 className="text-[#E8FC8B] text-sm font-semibold mb-3 flex items-center">
          <span className="w-2 h-2 bg-[#E8FC8B] rounded-full mr-2"></span>
          Middle Configuration
        </h5>
        {Object.entries(middle).map(([sport, config]) => (
          <div key={sport} className="mb-3 last:mb-0">
            <span className="text-white font-medium text-sm bg-[#1B2D1A] px-2 py-1 rounded-md">
              {sport}
            </span>
            <div className="ml-4 mt-2 space-y-1">
              {Object.entries(config).map(([key, value]) => (
                <div key={key} className="text-xs text-gray-300 flex justify-between">
                  <span className="font-medium">{key}:</span> 
                  <span>Max Value: {value.maximumValue}, Max Profit: {value.maximumProfit}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Group properties for better organization
  const basicInfo = {
    'Bookie': bookie.bookie,
    'Priority': bookie.priority,
    'Country': bookie.country,
    'OG Bookie': bookie.ogBookie
  };

  const loginSettings = {
    'Max Logins': bookie.maxlogins || bookie.maxLogins,
    'Login Interval': bookie.logInInterval,
    'Max Days Per Proxy': bookie.maxDaysPerProxy,
    'Token Refresh Interval': bookie.tokenRefreshInterval
  };

  const profitSettings = {
    'Minimum Profit': bookie.minimumProfit,
    'Maximum Profit': bookie.maximumProfit,
    'Minimum Stake': bookie.minimumStake
  };

  const thresholds = {
    'Hard Limit': bookie.hardLimit,
    'Probability Threshold': bookie.probabilityThreshold,
    'Max Bet Exceeded Threshold': bookie.maxBetExceededThreshold,
    'Rebet Liquidity Score Threshold': bookie.rebetLiquidityScoreThreshold
  };

  const flags = {
    'Should Round': bookie.shouldRound,
    'Needs Protection': bookie.needsProtection,
    'Needs Protection for Second Bet': bookie.needsProtectionForSecondBet,
    'Needs Extended Balance Check': bookie.needsExtendedBalanceCheck,
    'Tracing': bookie.tracing,
    'Debug Logging Enabled': bookie.debugLoggingEnabled,
    'No Balance Restriction': bookie.noBalanceRestriction,
    'Solo': bookie.solo,
    'Blacklisted': bookie.blacklisted
  };

  return (
    <div className="bg-[#1B2D1A] p-5 rounded-xl hover:bg-[#2A3C29] transition-all duration-300 shadow-lg hover:shadow-xl">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <h3 className="text-xl font-bold text-white mb-2 capitalize tracking-wide">
            {bookie.bookie}
          </h3>
          <div className="flex flex-wrap gap-2">
            {bookie.country && (
              <span className="inline-flex items-center px-3 py-1 bg-white text-[#1B2D1A] text-xs font-semibold rounded-full">
                {bookie.country}
              </span>
            )}
            {bookie.priority !== undefined && (
              <span className="inline-flex items-center px-3 py-1 bg-purple-500 text-white text-xs font-semibold rounded-full">
                Priority: {bookie.priority}
              </span>
            )}
          </div>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 text-white hover:text-[#E8FC8B] hover:bg-[#2A3C29] rounded-lg transition-all duration-200 group"
            title={isExpanded ? 'Show less' : 'Show more'}
          >
            {isExpanded ? (
              <ChevronUp size={18} className="group-hover:scale-110 transition-transform" />
            ) : (
              <ChevronDown size={18} className="group-hover:scale-110 transition-transform" />
            )}
          </button>
          <button
            onClick={onEdit}
            className="p-2 text-emerald-400 hover:text-emerald-300 hover:bg-[#2A3C29] rounded-lg transition-all duration-200 group"
            title={t('edit')}
          >
            <Edit3 size={18} className="group-hover:scale-110 transition-transform" />
          </button>
        </div>
      </div>

      {/* Quick Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <div className="bg-[#0F1A0E] p-3 rounded-lg">
          <span className="text-gray-400 font-medium text-sm block mb-1">Max Logins</span>
          <span className="text-white text-lg font-semibold">{bookie.maxlogins || bookie.maxLogins || 'N/A'}</span>
        </div>
        <div className="bg-[#0F1A0E] p-3 rounded-lg">
          <span className="text-gray-400 font-medium text-sm block mb-1">Login Interval</span>
          <span className="text-white text-lg font-semibold">{bookie.logInInterval || 'N/A'}</span>
        </div>
      </div>

      {/* Clones Preview */}
      <div className="mb-4 p-3 bg-[#0F1A0E] rounded-lg">
        <span className="text-gray-400 font-medium text-sm block mb-2">Clones</span>
        {bookie.clones && bookie.clones.length > 0 ? (
          <div className="flex items-center space-x-2">
            <span className="text-white text-lg font-semibold">
              {bookie.clones.length}
            </span>
            <span className="text-gray-400 text-sm">clone(s) configured</span>
          </div>
        ) : (
          <span className="text-gray-400 text-sm">No clones configured</span>
        )}
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="space-y-5 pt-4 border-t border-[#2A3C29]">
          {/* Basic Info */}
          <div className="bg-[#0F1A0E] p-4 rounded-lg">
            <h5 className="text-[#E8FC8B] text-sm font-semibold mb-3 flex items-center">
              <span className="w-2 h-2 bg-[#E8FC8B] rounded-full mr-2"></span>
              Basic Information
            </h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {Object.entries(basicInfo).map(([key, value]) => 
                value !== undefined && (
                  <div key={key} className="flex justify-between items-center py-1">
                    <span className="text-gray-400 text-sm">{key}:</span>
                    <span className="text-white font-medium">{value}</span>
                  </div>
                )
              )}
            </div>
          </div>

          {/* Login Settings */}
          <div className="bg-[#0F1A0E] p-4 rounded-lg">
            <h5 className="text-[#E8FC8B] text-sm font-semibold mb-3 flex items-center">
              <span className="w-2 h-2 bg-[#E8FC8B] rounded-full mr-2"></span>
              Login Settings
            </h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {Object.entries(loginSettings).map(([key, value]) => 
                value !== undefined && (
                  <div key={key} className="flex justify-between items-center py-1">
                    <span className="text-gray-400 text-sm">{key}:</span>
                    <span className="text-white font-medium">{value}</span>
                  </div>
                )
              )}
            </div>
          </div>

          {/* Profit Settings */}
          {Object.values(profitSettings).some(v => v !== undefined) && (
            <div className="bg-[#0F1A0E] p-4 rounded-lg">
              <h5 className="text-[#E8FC8B] text-sm font-semibold mb-3 flex items-center">
                <span className="w-2 h-2 bg-[#E8FC8B] rounded-full mr-2"></span>
                Profit Settings
              </h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Object.entries(profitSettings).map(([key, value]) => 
                  value !== undefined && (
                    <div key={key} className="flex justify-between items-center py-1">
                      <span className="text-gray-400 text-sm">{key}:</span>
                      <span className="text-white font-medium">{value}</span>
                    </div>
                  )
                )}
              </div>
            </div>
          )}

          {/* Thresholds */}
          {Object.values(thresholds).some(v => v !== undefined) && (
            <div className="bg-[#0F1A0E] p-4 rounded-lg">
              <h5 className="text-[#E8FC8B] text-sm font-semibold mb-3 flex items-center">
                <span className="w-2 h-2 bg-[#E8FC8B] rounded-full mr-2"></span>
                Thresholds
              </h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Object.entries(thresholds).map(([key, value]) => 
                  value !== undefined && (
                    <div key={key} className="flex justify-between items-center py-1">
                      <span className="text-gray-400 text-sm">{key}:</span>
                      <span className="text-white font-medium">{value}</span>
                    </div>
                  )
                )}
              </div>
            </div>
          )}

          {/* Flags */}
          <div className="bg-[#0F1A0E] p-4 rounded-lg">
            <h5 className="text-[#E8FC8B] text-sm font-semibold mb-3 flex items-center">
              <span className="w-2 h-2 bg-[#E8FC8B] rounded-full mr-2"></span>
              Configuration Flags
            </h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {Object.entries(flags).map(([key, value]) => 
                value !== undefined && (
                  <div key={key} className="flex justify-between items-center py-1">
                    <span className="text-gray-400 text-sm">{key}:</span>
                    {renderBooleanValue(value)}
                  </div>
                )
              )}
            </div>
          </div>

          {/* Clones Details */}
          {bookie.clones && bookie.clones.length > 0 && (
            <div className="bg-[#0F1A0E] p-4 rounded-lg">
              <h5 className="text-[#E8FC8B] text-sm font-semibold mb-3 flex items-center">
                <span className="w-2 h-2 bg-[#E8FC8B] rounded-full mr-2"></span>
                Clone Details
              </h5>
              {renderClones(bookie.clones)}
            </div>
          )}

          {/* Middle Configuration */}
          {bookie.middle && renderMiddleConfiguration(bookie.middle)}
        </div>
      )}
    </div>
  );
};

export default BookieCard; 